import { useEffect, useRef, useState } from "react";
import {
  signInWithGoogleCredential,
  describeAuthError,
} from "../../../utils/auth-flow";
import {
  renderGoogleButton,
  isGoogleConfigured,
} from "../../../utils/google-identity";
import {
  detectInAppBrowser,
  openInExternalBrowser,
  copyCurrentURL,
} from "../../../utils/in-app-browser";
import "./login-sheet-style.scss";

//登入彈窗
//
//  從畫面底部滑出的 sheet，不是置中對話框 —— 手機上底部離拇指最近，
//  而且遮罩是半透明的，背景仍看得見使用者原本在看的內容。
//
//  登入按鈕由 Google Identity Services 官方渲染（renderGoogleButton），
//  不是我們自己畫的 —— 這是 Google 品牌規範的要求，官方按鈕也會自動
//  處理多語系與各種登入狀態。
//
//  in-app 瀏覽器（LINE / IG / FB / Threads 的內建瀏覽器）無法完成 Google 登入：
//  這些 WebView 會擋掉 storage、Google OAuth 也會擋部分 UA。
//  這是環境限制不是程式問題，唯一穩解是引導改用系統瀏覽器開啟。
//  對本站尤其重要 —— 命理內容很常被貼到 LINE 分享。
//
//  props:
//    open       是否顯示（受控，由呼叫端決定）
//    onClose    關閉
//    onSuccess  登入成功，帶回 user
//    message    說明文字，讓呼叫端能寫「登入後即可提問」之類的情境句
const LoginSheet = ({ open, onClose, onSuccess, message }) => {
  const btnRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [inApp, setInApp] = useState({ isInApp: false, app: "", appLabel: "" });
  const [copied, setCopied] = useState(false);

  //開啟時重置狀態並偵測環境
  //  每次開啟都重新偵測而不是只在 mount 時做一次：使用者可能已經照指示
  //  外開到系統瀏覽器了，重開時應該要看到正常的登入按鈕
  useEffect(() => {
    if (!open) return;
    setError("");
    setCopied(false);
    setInApp(detectInAppBrowser());
  }, [open]);

  //渲染 Google 官方按鈕
  //  in-app 瀏覽器時不渲染：那個環境按了也不會成功，給按鈕反而誤導
  useEffect(() => {
    if (!open || inApp.isInApp || !btnRef.current) return;

    if (!isGoogleConfigured()) {
      setError("登入服務尚未設定完成（client/.env 的 REACT_APP_GOOGLE_CLIENT_ID）");
      return;
    }

    let alive = true;
    const el = btnRef.current;

    renderGoogleButton(el, {
      //按鈕寬度跟著容器走，才不會在窄手機上超出畫面
      width: el.offsetWidth,
      onCredential: async (credential) => {
        if (!alive) return;
        setError("");
        setLoading(true);
        try {
          const user = await signInWithGoogleCredential(credential);
          if (alive) onSuccess?.(user);
        } catch (e) {
          if (alive) setError(describeAuthError(e));
        } finally {
          if (alive) setLoading(false);
        }
      },
    }).catch((e) => {
      if (alive) setError(e.message);
    });

    return () => {
      alive = false;
      //關閉後重開時 GIS 會再渲染一顆，不清掉會疊成兩顆按鈕
      el.innerHTML = "";
    };
  }, [open, inApp.isInApp, onSuccess]);

  //開啟時鎖住背景捲動，並支援 ESC 關閉
  useEffect(() => {
    if (!open) return;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (e) => {
      if (e.key === "Escape" && !loading) onClose?.();
    };
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open, loading, onClose]);

  if (!open) return null;

  const handleCopyLink = async () => {
    setCopied(await copyCurrentURL());
  };

  return (
    <div className="ls" role="dialog" aria-modal="true" aria-label="登入">
      <div className="ls__mask" onClick={loading ? undefined : onClose} />

      <div className="ls__panel">
        <div className="ls__handle" aria-hidden="true" />

        {inApp.isInApp ? (
          //—— in-app 瀏覽器：無法登入，引導外開 ——
          <>
            <h2 className="ls__title">請用瀏覽器開啟</h2>
            <p className="ls__message">
              偵測到你正在 {inApp.appLabel} 內開啟本站。
              <br />
              此 App 的內建瀏覽器無法完成 Google 登入，
              <br />
              請改用 Safari 或 Chrome 開啟這一頁。
            </p>

            {inApp.app === "line" ? (
              //LINE 有官方外開參數，可以一鍵處理
              <button
                type="button"
                className="ls__outline-btn"
                onClick={() => openInExternalBrowser(inApp.app)}
              >
                用系統瀏覽器開啟
              </button>
            ) : (
              //其他 App 沒有官方外開方式，只能教學 + 複製連結
              <>
                <p className="ls__steps">
                  點畫面右上角的「•••」或分享鈕，
                  <br />
                  選擇「在瀏覽器開啟」。
                </p>
                <button
                  type="button"
                  className="ls__outline-btn"
                  onClick={handleCopyLink}
                >
                  {copied ? "已複製連結 ✓" : "複製連結"}
                </button>
                {copied && (
                  <p className="ls__hint">
                    連結已複製，貼到 Safari 或 Chrome 開啟即可
                  </p>
                )}
              </>
            )}
          </>
        ) : (
          //—— 一般瀏覽器：Google 官方按鈕 ——
          <>
            <h2 className="ls__title">登入閻異閣</h2>
            <p className="ls__message">
              {message || "登入後即可查看你的報告與個人資料。"}
            </p>

            <div className="ls__google-slot">
              {/* GIS 會把官方按鈕塞進這個 div */}
              <div ref={btnRef} className="ls__google-btn" />

              {/* 登入處理中蓋一層，避免重複點擊 */}
              {loading && <div className="ls__google-busy">登入中…</div>}
            </div>

            {error && <p className="ls__error">{error}</p>}

            {/* 2026-10-28 拿掉「繼續即表示你同意服務條款與隱私權政策」（Kevin）。
                那兩個連結指向 /policy，而這條 route 不存在 —— 點下去開出來的是
                一個白頁的新分頁，比沒有連結更糟。
                兩份條文仍然從頁尾進得去（那裡開的是 policy-dialog）。 */}
          </>
        )}

        <button
          type="button"
          className="ls__cancel"
          onClick={onClose}
          disabled={loading}
        >
          稍後再說
        </button>
      </div>
    </div>
  );
};

export default LoginSheet;
